/**
 * PD-16777 — US bank routing number (ABA routing transit number) checks.
 *
 * A bank account saved with a wrong routing number is accepted by every
 * gateway's vault and only fails later, when a real payment is attempted and
 * the processor answers "INVALID ROUTING NUMBER". By then the customer has
 * left and staff are chasing a failed charge. Catching the typo while the
 * person is still on the form is far cheaper, so every bank-account entry
 * point (the CRM and storefront forms, and the server as a backstop) runs
 * these checks before anything reaches a gateway.
 *
 * What a structurally valid routing number looks like, per the Federal
 * Reserve's routing number rules:
 *
 *   1. Exactly nine digits.
 *   2. A valid first-two-digit prefix. The Fed assigns:
 *        00     the U.S. Government
 *        01–12  the twelve Federal Reserve districts (ordinary banks)
 *        21–32  thrift institutions (district + 20)
 *        61–72  electronic-only transactions (district + 60)
 *        80     traveler's checks
 *      Only 01–12, 21–32 and 61–72 identify a deposit account a business
 *      customer can be debited from, so 00 and 80 are refused along with
 *      every unassigned prefix. 00 is the Treasury's own range and 80 is
 *      issued for traveler's checks, which are not accounts at all.
 *   3. The 3-7-1 checksum: 3×(d1+d4+d7) + 7×(d2+d5+d8) + (d3+d6+d9) is a
 *      multiple of ten.
 *
 * The checksum alone is not enough. Motor City Ice's failing account used
 * 552077772, which passes the checksum but carries the unassigned prefix 55.
 *
 * Passing these checks does not prove the number belongs to a real bank, only
 * that it is not a typo. Gateways that verify the account itself (a small
 * verification charge that is immediately voided) catch the rest.
 */

/**
 * Routing numbers a payment gateway's TEST environment requires, which are
 * deliberately not real routing numbers.
 *
 * NMI's sandbox accepts exactly one bank account, routing 490000018 with
 * account 24413815, and rejects every other one ("Please use a valid test
 * card or test ACH account from the documentation"). Its prefix, 49, is not
 * a Fed-assigned range, so the strict check would make bank accounts
 * impossible to add on any NMI staging tenant. It is accepted everywhere
 * because a production gateway rejects it on its own (NMI's verification
 * charge declines it), and nobody types it by accident.
 *
 * Source: NMI Payment API documentation, "Testing Information".
 */
export const GATEWAY_TEST_ROUTING_NUMBERS: ReadonlySet<string> = new Set([
	'490000018',
]);

/**
 * The message every bank-account entry point shows for a routing number that
 * fails the checks below. Written for the person typing it (a customer on the
 * storefront or staff in the CRM), and free of gateway names so the storefront
 * may show it as-is.
 */
export const ROUTING_NUMBER_INVALID_MESSAGE =
	'Routing Number is not valid. Check the 9-digit number printed at the bottom left of a check.';

/**
 * The server's refusal of a bank account, when it is one written for the
 * person adding it: the routing number message above (the server's backstop),
 * or a sentence saying the bank account was not saved and why (NMI's
 * verification sale, which reports the processor's reason). Returned as it
 * should be shown, without NMI's REFID reference, or null for any other error.
 *
 * The CRM panel maps server errors through keyword checks that turned both
 * into "Unable to add payment method. Please verify your information and try
 * again.", which drops the reason and the instruction that fixes it, so the
 * panel asks this first. The crm-api tests for the NMI refusals assert their
 * messages are recognized here.
 */
export function getBankAccountRefusalMessage(
	message: string | null | undefined,
): string | null {
	if (message == null) {
		return null;
	}

	const cleaned = message.replace(/\s*REFID:\s*\d+/gi, '').trim();

	if (
		cleaned.startsWith(ROUTING_NUMBER_INVALID_MESSAGE) ||
		/\bbank account (?:was not saved|could not be verified)\b/i.test(cleaned)
	) {
		return cleaned;
	}

	return null;
}

/**
 * Prefix ranges (first two digits, inclusive) that identify a deposit
 * account. See the file header for why 00 and 80 are excluded.
 */
const VALID_PREFIX_RANGES: ReadonlyArray<readonly [number, number]> = [
	[1, 12],
	[21, 32],
	[61, 72],
];

const CHECKSUM_WEIGHTS: ReadonlyArray<number> = [3, 7, 1, 3, 7, 1, 3, 7, 1];

/**
 * Strip the separators people type or paste into a routing number: spaces
 * (including the ones a copied value carries at either end) and dashes.
 * Anything else is left in place so it fails validation rather than being
 * silently discarded — a letter in a routing number is a typo, not formatting.
 */
export function normalizeRoutingNumber(
	value: string | null | undefined,
): string {
	if (value == null) {
		return '';
	}

	return value.replace(/[\s-]/g, '');
}

/**
 * True when the value, once normalized, is a structurally valid ABA routing
 * number: nine digits, a Fed-assigned deposit-account prefix, and a passing
 * 3-7-1 checksum. Strict — gateway test numbers fail it. Use
 * `isAcceptedRoutingNumber` at entry points.
 */
export function isValidAbaRoutingNumber(
	value: string | null | undefined,
): boolean {
	const digits = normalizeRoutingNumber(value);

	if (!/^\d{9}$/.test(digits)) {
		return false;
	}

	const prefix = Number(digits.slice(0, 2));
	const hasValidPrefix = VALID_PREFIX_RANGES.some(
		([low, high]) => prefix >= low && prefix <= high,
	);

	if (!hasValidPrefix) {
		return false;
	}

	let sum = 0;

	for (let index = 0; index < CHECKSUM_WEIGHTS.length; index++) {
		sum += Number(digits.charAt(index)) * (CHECKSUM_WEIGHTS[index] ?? 0);
	}

	return sum % 10 === 0;
}

/**
 * Whether a bank-account entry point should let this routing number through:
 * a valid ABA routing number, or one of the gateway test numbers above.
 */
export function isAcceptedRoutingNumber(
	value: string | null | undefined,
): boolean {
	return (
		isValidAbaRoutingNumber(value) ||
		GATEWAY_TEST_ROUTING_NUMBERS.has(normalizeRoutingNumber(value))
	);
}
